import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { State } from 'reducers';
import { HasUrlSlug, getBuildingByUrlSlug } from 'selectors/buildingsSelectors';
import { isEqual, mapValues, omit, pickBy, values } from 'lodash';
import { DeviceId, EcsDevice, EcsExternalId, Group, GroupId, GroupType } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { ElevatorList } from '../elevatorList/ElevatorList';
import { DeviceList } from '../deviceList/DeviceList';
import { callApi } from 'apiClient';
import { ConfirmationDialog } from 'ui/shell/confirmationDialog/ConfirmationDialog';
import { AssignDeviceToElevatorDialog } from '../assignDeviceToElevatorDialog/AssignDeviceToElevatorDialog';
import { ErrorMessage, SuccessMessage } from 'ui/shared/messages';
import { Loading } from 'ui/shared/loading';
import { DebugLink } from 'ui/shared/DebugLink';
import { renameDevices } from 'logic/renameDevices';
import { toError } from 'shared/util/toError';

require('./ElevatorListPage.css');

function isElevatorDevice(d: EcsDevice) {
    return d.deviceClass === 'camera' ||
        d.deviceClass === 'display' ||
        d.deviceClass === 'relay';
}

export const ElevatorListPage = () => {
    const { t } = useTranslation();
    const match = useRouteMatch<HasUrlSlug>('/buildings/:urlSlug/elevators');
    const params = match ? match.params : {};
    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id;

    const [ inEditMode, setInEditMode ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState('');
    const [ successVisible, setSuccessVisible ] = useState(false);

    const [ isLoading, setIsLoading ] = useState(true);
    const [ isSaving, setIsSaving ] = useState(false);
    const [ devices, setDevices ] = useState<Record<DeviceId, EcsDevice>>({});
    const [ groups, setGroups ] = useState<Record<GroupId, Group>>({});
    const [ currentDevices, setCurrentDevices ] = useState<Record<DeviceId, EcsDevice>>({});
    const [ online, setOnline ] = useState<Record<DeviceId, boolean>>({});
    const [ externalIds, setExternalIds ] = useState<Record<GroupId, EcsExternalId[]>>({});
    const [ removedGroupIds, setRemovedGroupIds ] = useState<GroupId[]>([]);

    const [ deviceToDelete, setDeviceToDelete ] = useState<EcsDevice>();
    const [ deleteModalVisible, setDeleteModalVisible ] = useState(false);

    const [ deviceToAssign, setDeviceToAssign ] = useState<EcsDevice>();
    const [ assignModalVisible, setAssignModalVisible ] = useState(false);
    
    const machineRoom = values(groups).find(g => g.type === GroupType.MachineRoom);
    const machineRoomDevices = values(currentDevices).filter(d => d.groupId === machineRoom?.id);
    const unassignedDevices = values(currentDevices).filter(d => !d.groupId).filter(isElevatorDevice);

    const elevators = useMemo(() => {
        return values(groups).filter(g => g.type === GroupType.Elevator && removedGroupIds.indexOf(g.id) === -1);
    }, [ groups, removedGroupIds ]);

    const loadData = useCallback(async () => {
        setErrorMessage('');
        if (!buildingId) {
            return;
        }
        setIsLoading(true);
        try {
            const cacheBust = Math.random().toString(28).substr(2);
            const { devices, online, groups, externalIds } = await callApi('GET', `/api/buildings/${buildingId}/elevators?${cacheBust}`);
            setDevices(devices);
            setGroups(groups);
            setCurrentDevices(mapValues(devices));
            setOnline(online);
            setExternalIds(externalIds);
        }
        catch (e) {
            const err = toError(e);
            setErrorMessage(t([ err.message, 'SERVER_ERROR' ]));
        }
        finally {
            setIsLoading(false);
        }
    }, [ buildingId, setIsLoading, setDevices, setGroups, setCurrentDevices, setOnline, setExternalIds, setErrorMessage, t ]);

    const handleCancel = useCallback(() => {
        setErrorMessage('');
        setRemovedGroupIds([]);
        setCurrentDevices(devices);
        setInEditMode(false);
    }, [ devices, setInEditMode, setCurrentDevices, setErrorMessage, setRemovedGroupIds ]);

    const calculateNewDeviceNames = useCallback((devices: Record<DeviceId, EcsDevice>) => {
        return elevators.reduce((all, group) => {
            const devicesInThisElevator = pickBy(devices, d => d.groupId === group.id);
            return {
                ...all,
                ...renameDevices(group.displayName, devicesInThisElevator, t),
            };
        }, {});
    }, [ elevators, t ]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setErrorMessage('');
        try {
            const assignments = mapValues(currentDevices, d => d.groupId || null);
            const deviceNames = calculateNewDeviceNames(currentDevices);
            await callApi('PUT', `/api/buildings/${buildingId}/elevators`, { assignments, deviceNames, remove: removedGroupIds });
            setInEditMode(false);
            setSuccessVisible(true);
            setDevices(currentDevices);

            // Remove the selected groups from local data
            setGroups(groups => omit(groups, ...removedGroupIds));
            setRemovedGroupIds([]);
        }
        catch (e) {
            const err = toError(e);
            setErrorMessage(t([ err.message, 'SERVER_ERROR' ]));
        }
        finally {
            setIsSaving(false);
        }
    }, [ buildingId, currentDevices, removedGroupIds, setRemovedGroupIds, setInEditMode, setIsSaving, setDevices, setSuccessVisible, setErrorMessage, calculateNewDeviceNames, t ]);

    const assignDeviceToElevator = useCallback((deviceId: DeviceId, elevatorId: GroupId | null) => {
        setCurrentDevices(devices => {
            return mapValues(devices, d =>
                d.deviceId === deviceId
                    ? {
                        ...d,
                        groupId: elevatorId,
                    }
                    : d
            );
        });
    }, [ setCurrentDevices ]);

    const handleAssignRequest = useCallback((deviceId: DeviceId) => {
        const device = devices[deviceId];
        if (!device) return;
        setDeviceToAssign(device);

        // If only one elevator, just assign the device without prompting
        // (unless it's a relay device, which could be put in either the
        // elevator or the machine room)
        (elevators.length === 1 && device.deviceClass !== 'relay')
            ? assignDeviceToElevator(device.deviceId, elevators[0].id)
            : setAssignModalVisible(true);
    }, [ devices, elevators, setDeviceToAssign, setAssignModalVisible, assignDeviceToElevator ]);

    const handleAssignConfirmed = useCallback((elevatorId: GroupId) => {
        assignDeviceToElevator(deviceToAssign!.deviceId, elevatorId);
        setAssignModalVisible(false);
    }, [ deviceToAssign, setAssignModalVisible, assignDeviceToElevator ]);

    const handleRemove = useCallback((deviceId: DeviceId) => {
        assignDeviceToElevator(deviceId, null);
    }, [ assignDeviceToElevator ]);

    const handleRemoveGroup = useCallback((groupId: GroupId) => {
        setRemovedGroupIds(remove => [ ...remove, groupId ]);
    }, [ setRemovedGroupIds ]);

    const handleDeleteRequest = useCallback((deviceId: DeviceId) => {
        const device = devices[deviceId];
        if (device) {
            setDeviceToDelete(device);
            setDeleteModalVisible(true);
        }
    }, [ devices, setDeviceToDelete, setDeleteModalVisible ]);

    const handleDeleteConfirmed = useCallback(async () => {
        if (!buildingId || !deviceToDelete) {
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            await callApi('DELETE', `/api/buildings/${buildingId}/devices/${deviceToDelete.deviceId}`);
            setDevices(devices => omit(devices, deviceToDelete.deviceId));
            setCurrentDevices(devices => omit(devices, deviceToDelete.deviceId));
        }
        catch (e) {
            const err = toError(e);
            setErrorMessage(t([ err.message, 'SERVER_ERROR' ]));
            console.error(err);
        }
        finally {
            setDeleteModalVisible(false);
            setIsSaving(false);
        }
    }, [ buildingId, deviceToDelete, setDevices, setCurrentDevices, setIsSaving, setDeleteModalVisible, setErrorMessage, t ]);

    useEffect(() => {
        void loadData();
    }, [ loadData ]);

    const dirty = !isEqual(devices, currentDevices) || removedGroupIds.length > 0;
    
    return (
        <div id="elevator-list-page" className="page">
            <h1>
                { t('BUILDING.ELEVATORS.LINK') }
                <span style={ { marginLeft: '.5em', position: 'relative', top: '-.175em' } }>
                    { !inEditMode &&
                        <button
                            className="ui tiny basic icon button"
                            disabled={ isLoading }
                            onClick={ loadData }
                        >
                            <i className="ui grey refresh icon"/>
                        </button>
                    }
                </span>
            </h1>

            { successVisible &&
                <SuccessMessage
                    id="elevator-list-page-success"
                    message={ t('SAVED_MESSAGE') }
                    onClick={ () => setSuccessVisible(false) }
                />
            }

            <ElevatorList
                buildingId={ buildingId }
                editing={ inEditMode }
                dirty={ dirty }
                busy={ isLoading || isSaving }
                elevators={ elevators }
                devices={ currentDevices }
                online={ online }
                externalIds={ externalIds }
                onEdit={ () => setInEditMode(true) }
                onRemove={ handleRemove }
                onRemoveGroup={ handleRemoveGroup }
                onCancel={ handleCancel }
                onSave={ handleSave }
            />

            <h1>
                { t('BUILDING.ELEVATORS.MACHINE_ROOM') }
            </h1>
            <table className="ui single line compact table elevator-list">
                <colgroup>
                    <col style={ { width: '100%' } }/>
                </colgroup>
                <thead>
                    <tr>
                        <th>
                            { t('BUILDING.ELEVATORS.COLUMN.DEVICES') }
                        </th>
                    </tr>
                </thead>
                <tbody>
                    { isLoading && machineRoomDevices.length === 0 &&
                        <tr><td><Loading/></td></tr>
                    }
                    <tr><td>
                        <DeviceList
                            editing={ inEditMode }
                            devices={ machineRoomDevices }
                            online={ online }
                            busy={ isLoading || isSaving }
                            onRemove={ inEditMode ? handleRemove : undefined }
                        />
                    </td></tr>
                </tbody>
            </table>

            { unassignedDevices.length > 0 &&
                <Fragment>
                    <h1>
                        { t('BUILDING.ELEVATORS.UNASSIGNED_DEVICES') }
                    </h1>
                    <DeviceList
                        editing={ inEditMode }
                        devices={ unassignedDevices }
                        online={ online }
                        busy={ isLoading || isSaving }
                        onAssign={ handleAssignRequest }
                        onDelete={ handleDeleteRequest }
                    />
                </Fragment>
            }
            
            <DebugLink
                sql={
                    `--[Building]\n` +
                    `SELECT * FROM buildings WHERE id = '${buildingId}';\n\n` +
                    `--[Device Groups]\n` +
                    `SELECT * FROM device_groups WHERE building_id = '${buildingId}';\n\n` +
                    `--[Device Status]\n` +
                    `SELECT d.device_class, d.display_name, d.mac_address, s.*\nFROM ecs_device_status s,\n    devices d\nWHERE d.building_id = '${buildingId}'\n    AND s.device_id = d.id;\n`
                }
            />

            <ConfirmationDialog
                id="delete-device-confirmation-dialog"
                message={ t('BUILDING.ELEVATORS.DELETE_CONFIRM', [ deviceToDelete ? deviceToDelete.macAddress : '' ]) }
                visible={ deleteModalVisible }
                onClickNo={ () => setDeleteModalVisible(false) }
                onClickYes={ handleDeleteConfirmed }
            />
            { elevators.length > 0 &&
                <AssignDeviceToElevatorDialog
                    busy={ isSaving }
                    visible={ assignModalVisible }
                    elevators={ elevators }
                    machineRoom={ deviceToAssign && deviceToAssign.deviceClass === 'relay' ? machineRoom : null }
                    defaultElevatorId={ (deviceToAssign && deviceToAssign.groupId) || elevators[0].id }
                    onAssign={ handleAssignConfirmed }
                    onCancel={ () => setAssignModalVisible(false) }
                />
            }

            <ErrorMessage
                message={ errorMessage }
                onClick={ () => setErrorMessage('') }
            />
        </div>
    );
};
